import { useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import type { Order } from "../../../../api/orders";
import { useAuthUser } from "../../hooks/useAuthUser";
import { HarvestContext } from "../../store/HarvestContext";
import { OrderContext } from "../../store/OrderContext";
import { ModalContext } from "../../store/ModalContext";
import { SeasonContext } from "../../store/SeasonContext";
import { DeliveryMethod, type CartItem } from "../../utils/types";

import AvailableProductsList from "./components/AvailableProductsList";
import DeliveryOptions from "./components/DeliveryOptions";
import OrderPreview from "./components/OrderPreview";
import {
  getCartStockError,
  getUpdatedCart,
} from "./BusinessOrderCreationPage.helpers";
import FloatingMenu from "../../components/menu/FloatingMenu";

const BusinessOrderCreationPage = () => {
  const { harvestProducts } = useContext(HarvestContext);
  const { createOrder } = useContext(OrderContext);
  const [newOrder, setNewOrder] = useState<Order | null>(null);
  const { openModal, closeModal } = useContext(ModalContext);
  const { freeDeliveryThreshold, businessDeliveryPrice } =
    useContext(SeasonContext);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [deliveryMethod, setDeliveryMethod] = useState(DeliveryMethod.Shipping);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const printableRef = useRef<HTMLDivElement>(null);
  const { userId, details } = useAuthUser();
  const companyRegistrationNumber = details?.companyRegistrationNumber;
  const navigate = useNavigate();

  const activeCartItems = useMemo(
    () => cart.filter((cartItem) => cartItem.quantity > 0),
    [cart],
  );

  const cartStockError = useMemo(
    () => getCartStockError(activeCartItems, harvestProducts),
    [activeCartItems, harvestProducts],
  );

  const canPlaceOrder = activeCartItems.length > 0 && cartStockError === null;

  function handleOrder() {
    if (activeCartItems.length < 1) {
      openModal({
        title: "WARNING",
        body: <p>Please select minimum 1 product.</p>,
        showCloseButton: true,
      });
      return;
    }

    if (cartStockError) {
      openModal({
        title: "WARNING",
        body: <p>{cartStockError}</p>,
        showCloseButton: true,
      });
      return;
    }

    openModal({
      title: "Your ORDER",
      body: (
        <OrderPreview
          activeCartItems={activeCartItems}
          deliveryMethod={deliveryMethod}
          freeDeliveryThreshold={freeDeliveryThreshold}
          businessDeliveryPrice={businessDeliveryPrice}
        />
      ),
      buttons: [
        { text: "No", callback: closeModal },
        {
          text: "Yes, place order!",
          loadingText: "Placing order...",
          callback: confirmOrder,
        },
      ],
    });
  }

  function updateCart(productName: string, productAmount: string) {
    setCart((prev) => {
      const updatedCart = getUpdatedCart(
        prev,
        harvestProducts,
        productName,
        productAmount,
      );
      return updatedCart;
    });
  }

  async function confirmOrder() {
    if (isCreatingOrder) return;

    let grandTotal = 0;

    const orderItems = activeCartItems.map((cartItem: CartItem) => {
      const itemTotalPrice = cartItem.totalPrice;

      grandTotal += itemTotalPrice;

      return {
        productName: cartItem.productName,
        unitPrice: cartItem.unitPrice,
        quantity: cartItem.quantity,
        totalPrice: itemTotalPrice,
      };
    });

    if (!userId) {
      return;
    }

    if (!companyRegistrationNumber) {
      openModal({
        title: "WARNING",
        body: (
          <p>
            Your account is missing a company registration number. Please log
            out and log in again, or contact support.
          </p>
        ),
        showCloseButton: true,
      });
      return;
    }

    if (cartStockError) {
      openModal({
        title: "WARNING",
        body: <p>{cartStockError}</p>,
        showCloseButton: true,
      });
      return;
    }

    setIsCreatingOrder(true);

    try {
      const newOrder = await createOrder(
        companyRegistrationNumber,
        orderItems,
        deliveryMethod,
        grandTotal,
      );
      setNewOrder(newOrder);
      setCart([]);

      closeModal();
      navigate(`/dashboard/business/orders/${newOrder?.id}?new=true`);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to place order";
      openModal({
        title: "WARNING",
        body: (
          <div>
            <p>There was an error placing your order.</p>
            <p>{errorMessage}</p>
          </div>
        ),
        showCloseButton: true,
      });
    } finally {
      setIsCreatingOrder(false);
    }
  }
  const handlePrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: "Order Details",
  });
  return (
    <>
      <div className="page-container">
        <h1 className="business-header">New Order</h1>
        {newOrder && (
          <div className="btn-aligned-right-box">
            <button className="btn btn-aligned-right" onClick={handlePrint}>
              <span>🖨️</span> Print Or Save
            </button>
          </div>
        )}
        <div className="page-content">
          <>
            <div>
              <h2>Available Products:</h2>
              <AvailableProductsList
                onProductUpdate={(productName, productAmount) =>
                  updateCart(productName, productAmount)
                }
              />
            </div>
            <div>
              <DeliveryOptions
                deliveryMethod={deliveryMethod}
                freeDeliveryThreshold={freeDeliveryThreshold}
                businessDeliveryPrice={businessDeliveryPrice}
                onChange={(deliveryMethod) => {
                  setDeliveryMethod(deliveryMethod);
                }}
              />
            </div>

            {cartStockError && (
              <div className="info-card">
                <p className="error-message">{cartStockError}</p>
              </div>
            )}

            <div>
              <button
                className="btn order-btn"
                onClick={handleOrder}
                disabled={isCreatingOrder || !canPlaceOrder}
              >
                {isCreatingOrder ? "Creating order..." : "Order"}
              </button>
            </div>
          </>
        </div>
        <FloatingMenu logoutButton={true} homeButton={true} isBusiness={true} />
      </div>
    </>
  );
};

export default BusinessOrderCreationPage;
